import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const CHUNK_RELOAD_KEY = "querygen:chunk-reload-at";
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

type ChunkReloadRuntime = {
  getStorage: () => Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
  now: () => number;
  reload: () => void;
};

const browserRuntime: ChunkReloadRuntime = {
  getStorage: () => {
    try {
      return window.sessionStorage;
    } catch {
      return null;
    }
  },
  now: () => Date.now(),
  reload: () => window.location.reload(),
};

export function isRecoverableChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk .+ failed|ChunkLoadError/i.test(message);
}

export async function loadWithChunkRecovery<T>(
  importer: () => Promise<T>,
  runtime: ChunkReloadRuntime = browserRuntime,
): Promise<T> {
  try {
    const module = await importer();
    try {
      runtime.getStorage()?.removeItem(CHUNK_RELOAD_KEY);
    } catch {
      // Storage cleanup must not turn a successful import into a route failure.
    }
    return module;
  } catch (error) {
    if (!isRecoverableChunkLoadError(error)) throw error;

    let storage: ReturnType<ChunkReloadRuntime["getStorage"]>;
    try {
      storage = runtime.getStorage();
      if (!storage) throw error;

      const value = storage.getItem(CHUNK_RELOAD_KEY);
      const lastReloadAt = value === null ? null : Number(value);
      if (
        lastReloadAt !== null &&
        Number.isFinite(lastReloadAt) &&
        runtime.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS
      ) {
        throw error;
      }

      storage.setItem(CHUNK_RELOAD_KEY, String(runtime.now()));
    } catch {
      throw error;
    }

    runtime.reload();
    return new Promise<T>(() => undefined);
  }
}

export function lazyWithReload<T extends ComponentType<Record<string, unknown>>>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => loadWithChunkRecovery(importer));
}
